import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "../../components/common/layout/Index";
import StepOneForm from "../../components/seller/property/Auction/StepOneForm";
import Breadcrumb from "../../components/common/Breadcrumb";
import StepTwoForm from "../../components/seller/property/Auction/StepTwoForm";
import { createSlug } from "../../helpers";
import useTranslationHook from "../../components/hooks/useTranslationHook";
import sellerSlice, {
    handleAuctionFormStep,
    handleEditOnAuction,
} from "../../redux/slice/sellerSlice";
import { handleSwitchAccount } from "../../redux/action/profileAction";
import { ACCOUNT } from "../../utils/constants";
import { relayEmsignerCompletion } from "../../utils";

const sellerAction = sellerSlice.actions;

const AddAuctionDetail = () => {
    const navigate = useNavigate();
    const { t } = useTranslationHook();

    const step = useSelector((state) => state.seller.property.auction.step);
    const account = useSelector((state) => state.profile.account);

    const dispatch = useDispatch();
    const { property_name, state, country } = useSelector(
        (state) => state?.seller?.property?.propertyData || {}
    );
    const { id } = useParams();

    // Computed once at mount — true when this is a post-sign emsigner callback
    const [hasWorkflowId] = useState(() => {
        const raw = window.location.search;
        const norm = raw.slice(0, 1) + raw.slice(1).replace(/\?/g, "&");
        const result = !!new URLSearchParams(norm).get("workflowid");
        console.info("[AddAuctionDetail] mount — hasWorkflowId:", result, "| search:", raw);
        return result;
    });

    useLayoutEffect(() => {
        if (
            location.pathname.includes("seller") &&
            account !== ACCOUNT.Seller
        ) {
            dispatch(handleSwitchAccount(ACCOUNT.Seller));
            sessionStorage.setItem("Account", ACCOUNT.Seller);
        }
    }, []);

    // On full-page reload after emsigner callback: restore step=2 so Redux is consistent
    useEffect(() => {
        if (!hasWorkflowId) return;
        console.info("[AddAuctionDetail] workflowid in URL — dispatching step=2");
        dispatch(handleAuctionFormStep(2));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (window !== window.top || window.opener) {
            console.info(
                "[AddAuctionDetail] running inside iframe/new tab — relaying EMSIGNER_SIGNED, search:",
                window.location.search
            );
            relayEmsignerCompletion({
                type: "EMSIGNER_SIGNED",
                search: window.location.search,
            });
            return;
        }
        const raw = window.location.search;
        const normalized = raw.slice(0, 1) + raw.slice(1).replace(/\?/g, "&");
        if (new URLSearchParams(normalized).get("workflowid")) {
            console.info("[AddAuctionDetail] navigation guard — workflowid present, skipping redirect");
            return;
        }
        console.info("[AddAuctionDetail] navigation guard — step:", step);
        !step && navigate("/my-properties");
    }, [step, navigate]);

    useEffect(() => {
        return () => {
            console.info("[AddAuctionDetail] unmount — resetting auction state");
            dispatch(handleAuctionFormStep(1));
            dispatch(handleEditOnAuction(false));
            dispatch(sellerAction.handlePropertyData({}));
        };
    }, []);

    return (
        <>
            <Helmet>
                <meta
                    name="keywords"
                    content={t(
                        "Bidhome-Adres, CRE, brokers, investment, commercial real estate, sales, auction"
                    )}
                />
                <meta
                    name="description"
                    content={t(
                        "Bidhome-Adres brings buyers, sellers, and brokers together to efficiently market and close commercial real estate deals in online CRE auctions."
                    )}
                />
                <title>{t("Property Auction - Projects")}</title>
            </Helmet>
            <Layout>
                <Breadcrumb
                    links={[
                        { url: "/", name: t("Home") },
                        { url: "/my-properties", name: t("My Properties") },
                        {
                            url: `/seller/property/detail/${createSlug(
                                id,
                                property_name + " " + country + " " + country
                            )}`,
                            name: t(property_name || ""),
                        },
                        { url: "/my-properties", name: t("Put it on Auction") },
                    ]}
                />
                {step === 1 && !hasWorkflowId && <StepOneForm />}
                {(step === 2 || hasWorkflowId) && <StepTwoForm />}
            </Layout>
        </>
    );
};

export default AddAuctionDetail;
